import { RefreshCw, TriangleAlert } from 'lucide-react'
import type { ReactElement } from 'react'

import { Button } from '@/components/ui/button'
import { errorMessage } from '@/lib/api'
import { cn } from '@/lib/utils'

export interface ErrorStateProps {
  readonly titulo?: string
  /** Erro cru vindo da query/mutação. A mensagem em pt-BR é extraída dele. */
  readonly erro?: unknown
  /** Sobrepõe a mensagem derivada do erro. */
  readonly mensagem?: string
  /** Identificador do trace para suporte, quando a API mandar um. */
  readonly traceId?: string
  readonly aoTentarNovamente?: () => void
  readonly className?: string
}

/**
 * Estado de erro: mensagem em pt-BR + botão de tentar de novo. O terceiro
 * estado obrigatório da DoD.
 *
 * O ícone é âmbar (`--warning-fg`), nunca amarelo da marca: amarelo aqui é
 * identidade, não alerta. O token de TEXTO (`-fg`) e não o de preenchimento
 * porque este componente é o `errorComponent` do router: ele aparece no tema
 * claro, onde o âmbar calibrado para preto dá 2,15:1 sobre branco.
 */
export function ErrorState({
  titulo = 'Não foi possível carregar',
  erro,
  mensagem,
  traceId,
  aoTentarNovamente,
  className,
}: ErrorStateProps): ReactElement {
  const texto = mensagem ?? errorMessage(erro)

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-border',
        'bg-surface px-6 py-14 text-center',
        className,
      )}
    >
      <span className="flex size-11 items-center justify-center rounded-full bg-warning/10 text-warning-fg">
        <TriangleAlert className="size-5" aria-hidden="true" />
      </span>
      <h2 className="text-base font-semibold text-foreground">{titulo}</h2>
      <p className="max-w-prose text-sm text-fg-secondary">{texto}</p>
      {traceId === undefined ? null : (
        <p className="text-xs text-fg-muted">
          Código para o suporte: <span className="tabular-money">{traceId}</span>
        </p>
      )}
      {aoTentarNovamente === undefined ? null : (
        <Button variant="secondary" onClick={aoTentarNovamente} className="mt-1">
          <RefreshCw className="size-4" aria-hidden="true" />
          Tentar de novo
        </Button>
      )}
    </div>
  )
}
